import { Controller } from "@hotwired/stimulus"

// CP2020 equipment cost totals — a live subtotal per equipment list (weapons,
// armor, cyberware, chips, gear) plus a grand total. Nothing is stored.
//
// PULL aggregator (mirrors cp2020--computed-stats): it OWNS *when* to compute — on
// load, when an editor connects, and on any editor's change broadcast — and ASKS
// each editor for its subtotal() through an outlet. Each editor still owns its own
// cost rule; this holds none. recompute() is FUNCTIONAL: it derives every value
// fresh from the currently-connected editors, holding no accumulated state.
//
// Two reasons for pull over "editors emit on connect": (1) order-independence —
// outletConnected fires whenever an editor is ready, so this card need not connect
// first (reordering the cards can't silently blank the totals); (2) the ask is
// await-tolerant, so a subtotal that ever needs to initialize async just can.
const EDITORS = [ "weapons", "armor", "cyberware", "chips", "gear" ]
const cap = (s) => s[0].toUpperCase() + s.slice(1)

export default class extends Controller {
  static targets = [ ...EDITORS, "total" ]
  static outlets = EDITORS.map((e) => `cp2020--${e}`)

  connect() {
    this.recompute()
  }

  // Each editor connecting is a readiness signal — recompute so its initial
  // (server-rendered) subtotal lands. Stimulus names one callback per outlet.
  cp2020WeaponsOutletConnected() { this.recompute() }
  cp2020ArmorOutletConnected() { this.recompute() }
  cp2020CyberwareOutletConnected() { this.recompute() }
  cp2020ChipsOutletConnected() { this.recompute() }
  cp2020GearOutletConnected() { this.recompute() }

  // The ONE method for load, editor-connect, and change broadcasts: ask every
  // connected editor for its subtotal (await — it may initialize async), sum,
  // render. Editors not present on this sheet have no outlet and contribute 0.
  async recompute() {
    let total = 0
    for (const type of EDITORS) {
      const outlet = this[`cp2020${cap(type)}Outlets`][0]
      const value = outlet ? Number(await outlet.subtotal()) || 0 : 0
      total += value
      this.set(type, value)
    }
    this.set("total", total)
  }

  set(name, value) {
    const has = `has${cap(name)}Target`
    if (this[has]) this[`${name}Target`].textContent = `${value}eb`
  }
}
